// The chat's state. The conversation is kept exactly as Anthropic sees it, so
// what the panel shows and what Claude remembers can never drift apart. Every
// chat is kept on the board, listed beside the chat (ChatList) and read only
// when opened; each agent's open chat is kept in this browser too.

import { create } from "zustand";
import type { BetaContentBlock, BetaContentBlockParam, BetaMessageParam, BetaToolResultBlockParam } from "@anthropic-ai/sdk/resources/beta/messages/messages";
import type { AgentQuestion, Board, ChatMeta, Entry, ID, Item } from "../../shared/types";
import { get as appGet, toast, useStore as useApp } from "../state/store";
import { keptCopy, listChats, onSaved, patchChat, readChat, removeChat, saveNow, saveSoon, tookAt, unsaved, forgetSaves, type ChatSave, type Saved } from "./chats";
import { loadConfig, modelFor, NO_USAGE, saveKey, saveSettings, type ChatConfig, type Effort, type ModelInfo, type Provider, type Usage } from "./config";
import { newId } from "../../shared/ids";
import { KEEP_PICTURES, type MadePicture } from "./pictures";
import { readStored, readStoredJson, removeStored, writeStored, writeStoredJson } from "../lib/stored";

export type ToolStatus = "running" | "waiting" | "ok" | "error" | "declined" | "skipped";

export interface ToolState {
  status: ToolStatus;
  text?: string;
  /** whether what the call changed was on the user's screen when its reply was first shown done: then the reply has no button to it (Features 3) */
  here?: boolean;
  /** it asked the user first (Features 4: a hands-free run shows each call that still asked): why, or "yes" for any other ask */
  asked?: string;
}

/** One turn of the user's: what Claude changed, so it can be put back. */
export interface Run {
  id: string;
  at: number; // index of the user message that started it
  end: number; // index of its last message
  items: Record<ID, Item | null>; // what each touched item was before; null if Claude made it
  boards: Record<ID, Board | null>;
  /** what each touched entry (a doc board's page) was before; null if made in the turn. Older saved turns have none */
  entries?: Record<ID, Entry | null>;
  /** a slide as the turn left it (S5): its undo takes back only what the turn changed, by box and by field */
  slidesAfter?: Record<ID, Entry>;
  /** the computer's turn it followed, so a hands-free run's line in Board activity can undo it (Features 4) */
  turn?: string;
  undone: boolean;
}

export interface Approval {
  toolId: string;
  title: string;
  detail: string | null;
  url: string | null;
  /** in a hands-free run, why it still asks (Features 4) */
  why?: string;
}

export type { MadePicture };

export interface ChatError {
  title: string;
  body?: string | null;
  /** what its button does: change the key, try again, pick a model, start a new chat, or keep going */
  action?: "key" | "retry" | "model" | "new" | "more" | null;
}

export interface ChatState {
  open: boolean;
  config: ChatConfig;
  workspaceId: string | null;
  status: "idle" | "running";
  messages: BetaMessageParam[];
  draft: BetaContentBlock[] | null;
  stopped: string | null; // text Claude was writing when the user stopped it
  tools: Record<string, ToolState>;
  approval: Approval | null;
  error: ChatError | null;
  runs: Run[];
  usage: Usage;
  queued: string | null; // asked for before the key was in
  /** when the waiting message was asked for: one that waited over an hour never goes by itself (Fixes 8) */
  queuedAt: number | null;
  /** a waiting message that waited too long, put back in the box: the box says it was not sent while it holds it */
  unsent: string | null;
  prefill: string | null; // put in the box for the user to finish, not sent
  /** words the user said rather than typed, waiting to go: the message they become wears a mic */
  spoken: string | null;
  queuedAgent: string | null; // the waiting request, worded for the user's own agent
  hosts: string[]; // places the user said Claude may read from, this chat
  /**
   * Hands-free for this chat (Features 4): its own switch in the model menu, kept with the chat. Null (or left out) for
   * a new chat whose switch nobody touched yet, which follows the default in Settings, Agents until its first message
   */
  handsFree?: boolean | null;
  /**
   * What Anthropic's model list says the chat's key can use, read when the picker opens. known is null
   * while it is being read, and after a read that failed, so that key's list is not asked for again until
   * this is cleared; null when there is no list, which the next open of the picker reads.
   */
  models: { key: string; known: string[] | null; newer: ModelInfo[] } | null;
  /** asked for from the model menu: a key for the other chat, Claude or Codex */
  keyFor: Provider | null;
  /** the last pictures Codex made in this chat, oldest first: a theme wears the one made for it, or one it names */
  pictures: MadePicture[];
  /** the agent's own question waiting for the user's answer: turn is the computer's turn, null on an API key */
  question: { turn: string | null; id: string; toolId: string; questions: AgentQuestion[] } | null;
  /** the model this chat runs on when it is not its agent's default: picked in its menu, and kept with the chat (Features 4) */
  chatModel: string | null;
  /** this conversation's own id, which it keeps on the board, and when it was last written in */
  chatId: string;
  chatAt: number;
  /**
   * The board this conversation is on (Fixes 9, 28 Oct: a chat from one board showed on another, and a request typed
   * there renamed the wrong board). Each board shows its own chat: a chat's turns act on its board, and it is kept as
   * that board's. Null for a chat kept before chats had one, until it is opened on a board
   */
  chatBoard: string | null;
  /** a board's chat being read from the board as that board opens, while the panel waits for it */
  opening: string | null;
  /**
   * The board the chat on screen opened itself (Fixes 9 fix round: its agent made or showed it, or a link in its reply
   * was followed), while the user stays there: the chat stays on screen, and what is typed next is about this board
   */
  heldOn: string | null;
  /** the computer's turn running in this conversation, while one does: a chat with one can be left, and it goes on */
  turn: string | null;
  /** every chat on the board, either agent's, the most recently written first: what the Chats list shows */
  chats: ChatMeta[];
  /** why the list could not be read from the board, while it could not */
  chatsError: string | null;
  /** chats left while their reply runs on the computer, either agent's, for the list and the agent switch to mark */
  away: AwayChat[];
}

/** A chat left while its reply runs: what the panel says of it. */
export interface AwayChat {
  id: string;
  provider: Provider;
  title: string;
  /** working on it, or waiting on the user's answer or yes */
  state: "working" | "needs_you";
}

export const useChat = create<ChatState>(() => ({
  open: false,
  config: loadConfig(),
  workspaceId: null,
  status: "idle",
  messages: [],
  draft: null,
  stopped: null,
  tools: {},
  approval: null,
  question: null,
  error: null,
  runs: [],
  usage: NO_USAGE,
  queued: null,
  queuedAt: null,
  unsent: null,
  prefill: null,
  spoken: null,
  queuedAgent: null,
  hosts: [],
  handsFree: null,
  models: null,
  keyFor: null,
  pictures: [],
  chatModel: null,
  chatId: newId("c_"),
  chatAt: Date.now(),
  chatBoard: null,
  opening: null,
  heldOn: null,
  turn: null,
  chats: [],
  chatsError: null,
  away: [],
}));

export const chat = {
  get: () => useChat.getState(),
  set: useChat.setState,
};

// the models chosen in Settings, here or on another device, as the board sends them: a chat not mid-reply starts on them
let modelsSeen = "null";
useApp.subscribe((s) => {
  const now = JSON.stringify(s.runner?.agentModels ?? null);
  if (now === modelsSeen) return;
  modelsSeen = now;
  if (chat.get().status !== "running") chat.set({ config: loadConfig() });
});

// ---------------------------------------------------------------------------
// opening it from anywhere in the app

/**
 * Open the panel, and if there is a message, send it (or keep it until the
 * key is in). With send: false it goes in the box for the user to finish.
 */
export function openChat(message?: string, opts: { send?: boolean; agent?: string; spoken?: boolean } = {}) {
  // a request is for the chat as it is: a key screen opened from the model menu closes, so the turn runs
  // where the user can see it (and answer what it asks), never behind that screen
  chat.set({ open: true, queuedAgent: opts.agent ?? null, ...(message?.trim() ? { keyFor: null } : {}) });
  if (opts.send === false) {
    if (message) chat.set({ prefill: message });
    return;
  }
  if (message?.trim()) chat.set({ queued: message.trim(), queuedAt: Date.now(), spoken: opts.spoken ? message.trim() : null });
}

export function closeChat() {
  chat.set({ open: false });
}

export function toggleChat() {
  chat.set((s) => ({ open: !s.open }));
}

// ---------------------------------------------------------------------------
// keeping the conversation: every chat on the board (David, 19 Oct 2026: they follow him to every device), and each
// agent's open chat in this browser too, to open at once and to reach the board if the page closed first

/** Each agent's open chat in this browser: Claude's and Codex's are two conversations, not one. */
const SAVED = (ws: string, p: Provider = chat.get().config.provider) => `cb:chat:${ws}:${p}`;
/** Where the one chat was kept before each agent had its own. */
const SAVED_BEFORE = (ws: string) => "cb:chat:" + ws;
const MAX_SAVED = 1_500_000;
/** The most a chat can hold and still be kept on the board, with room to spare under the board's own limit. */
const BOARD_ROOM = 1_800_000;
/** a step's output kept with a chat that ran on a computer: the agent there keeps the whole of it */
const KEPT_OUTPUT = 2_000;

export type { Saved };

/** How many messages each conversation had when it was last saved: a save with more is a new last use. */
const writtenAt = new Map<string, number>();

/**
 * A saved conversation, as it comes back: nothing in it runs again. board: the board it is opened on, when that is
 * known apart from the copy (one kept before chats had a board takes the board it is opened on)
 */
function savedConvo(saved: Partial<Saved>, board?: string | null) {
  const tools: Record<string, ToolState> = {};
  // nothing runs after a reload: a call saved as running or waiting for the user's yes was stopped
  for (const [id, t] of Object.entries(saved.tools ?? {})) if (t) tools[id] = t.status === "running" || t.status === "waiting" ? { status: "skipped" } : (t as ToolState);
  const messages = Array.isArray(saved.messages) ? saved.messages : [];
  const chatId = typeof saved.id === "string" && saved.id ? saved.id : newId("c_");
  writtenAt.set(chatId, messages.length);
  return {
    messages,
    tools,
    runs: Array.isArray(saved.runs) ? (saved.runs as Run[]) : [],
    // a chat from before Hands-free asks as it did; a new one nobody sent anything in yet follows the default
    handsFree: typeof saved.handsFree === "boolean" ? saved.handsFree : messages.length ? false : null,
    // a chat saved before the counts grew keeps its numbers and starts the new ones at zero
    usage: { ...NO_USAGE, ...(saved.usage ?? {}) },
    hosts: Array.isArray(saved.hosts) ? saved.hosts : [],
    pictures: Array.isArray(saved.pictures) ? saved.pictures.filter((p) => typeof p?.key === "string").map((p) => ({ ...p, age: Number(p.age) || 0 })).slice(-KEEP_PICTURES) : [],
    chatId,
    chatAt: typeof saved.at === "number" ? saved.at : Date.now(),
    chatBoard: board !== undefined ? board : typeof saved.board === "string" ? saved.board : null,
    // the model it was given in its menu, if it was: a chat opened again goes on on it
    chatModel: typeof saved.model === "string" && /^(claude|gpt)-[a-z0-9.-]{2,40}$/.test(saved.model) ? saved.model : null,
    draft: null,
    stopped: null,
    error: null,
    approval: null,
    question: null,
    turn: null,
  };
}

/** A saved conversation, put back as the chat. */
function applySaved(saved: Partial<Saved>, board?: string | null) {
  chat.set({ ...savedConvo(saved, board), opening: null, heldOn: null });
}

/** The boards whose chats this page has read from the board, and the first read, which following a reply waits on. */
const listed = new Set<string>();
let listing: Promise<void> | null = null;

export function loadConversation(workspaceId: string | null, again = false) {
  if (!workspaceId || (!again && chat.get().workspaceId === workspaceId)) return;
  let saved: Partial<Saved> = {};
  try {
    // the one chat from before goes to whoever wrote it, once
    const before = localStorage.getItem(SAVED_BEFORE(workspaceId));
    if (before) {
      const old = JSON.parse(before) as Partial<Saved>;
      const owner = (Array.isArray(old.messages) && writtenBy(old.messages)) || chat.get().config.provider;
      if (!localStorage.getItem(SAVED(workspaceId, owner))) localStorage.setItem(SAVED(workspaceId, owner), before);
      localStorage.removeItem(SAVED_BEFORE(workspaceId));
    }
    saved = JSON.parse(localStorage.getItem(SAVED(workspaceId)) ?? "{}") as Partial<Saved>;
  } catch {
    saved = {};
  }
  if (chat.get().workspaceId !== workspaceId) {
    chat.set({ workspaceId, chats: [], chatsError: null });
    // the list goes with it, so it is read again: until then, where a chat kept here belongs is not known
    known.delete(workspaceId);
    listed.delete(workspaceId);
  }
  // this agent's chat open last in this browser comes back only on its own board (Fixes 9, 28 Oct): on any other, and
  // for one kept before chats had a board, the board's own chat is picked (followBoard) and a new one waits for it
  const board = appGet().boardId ?? null;
  // ...but one this browser kept before chats had a board, that never reached the board (it has no id: a key's chat
  // from before 19 Oct), is this browser's alone and nobody else's: it opens on the board it is first opened on, and
  // takes that board, rather than going out of sight (Fixes 9 fix round)
  const ownOnly = !!board && typeof saved.board !== "string" && !saved.id && Array.isArray(saved.messages) && saved.messages.length > 0;
  const mine = !board || (typeof saved.board === "string" && saved.board === board) || ownOnly;
  if (mine) applySaved(saved, ownOnly ? board : undefined);
  else {
    applySaved({}, board);
    placeholder = chat.get().chatId;
  }
  // the first time this page opens the board's chats: the list, and the chats this browser kept before they were on
  // the board; after that, the chat now on screen checked against the board's copy
  if (!listed.has(workspaceId)) {
    listed.add(workspaceId);
    listing = readChatList(workspaceId);
  } else void checkBoardCopy();
  // the chat now in front of the user: a reply in it is read
  markSeen(chat.get().chatId);
  // one left while its reply runs comes back as it is now, the reply still going into it
  if (resume(chat.get().chatId)) return;
  // a turn is saved before its tool calls are answered, so a reload while one waited leaves it open
  closeOpenCalls();
  // the board's own chat, when this one was only a stand-in for it
  if (!mine) followBoard();
}

/** The board's chats, for the list: read when the page opens the board, and again when its link comes back. */
export async function readChatList(ws: string | null = chat.get().workspaceId) {
  if (!ws) return;
  let list: ChatMeta[];
  const asked = Date.now();
  try {
    list = await listChats();
  } catch (e) {
    if (chat.get().workspaceId === ws) chat.set({ chatsError: (e as Error).message || "Could not read your chats from the board." });
    return;
  }
  if (chat.get().workspaceId !== ws) return;
  chat.set({ chats: list, chatsError: null });
  known.add(ws);
  // a chat this page saw reach the board before the list was asked for, and not in it, was deleted (here a link that
  // dropped missed it): its copies here go
  const listedNow = new Set(list.map((c) => c.id));
  const ids = new Set([chat.get().chatId, ...(["anthropic", "openai"] as Provider[]).map((p) => readKept(SAVED(ws, p))?.id)]);
  for (const id of ids) if (id && !listedNow.has(id) && (tookAt(id) ?? Infinity) < asked) forgetHere(id, true);
  markSeen(chat.get().chatId);
  moveToBoard(ws);
  void checkBoardCopy();
  // with the list, a board this browser kept no chat for opens on its newest
  followBoard();
}

/** A chat as the board keeps it: one that ran on a computer with each step's output cut short, which the agent there keeps whole. */
function boardBody(s: Convo, at: number = s.chatAt): Saved {
  const onComputer = s.messages.some((m) => (m as { runner?: unknown }).runner);
  const tools = onComputer ? Object.fromEntries(Object.entries(s.tools).map(([id, t]) => [id, t.text && t.text.length > KEPT_OUTPUT ? { ...t, text: t.text.slice(0, KEPT_OUTPUT) + "…" } : t])) : s.tools;
  return { messages: withoutPictures(s.messages), tools, runs: s.runs, usage: s.usage, hosts: s.hosts, pictures: s.pictures, id: s.chatId, at, ...(s.chatModel ? { model: s.chatModel } : {}), ...(typeof s.handsFree === "boolean" ? { handsFree: s.handsFree } : {}), ...(s.chatBoard ? { board: s.chatBoard } : {}) };
}

/**
 * A chat as it is kept, here and on the board: a picture the user sent is a line of words in its place, never the
 * picture (Features 1, 20 Oct). This browser keeps its small preview to show; the board's copy keeps not even that.
 */
const PICTURE_KEPT = "(A picture was here. Pictures are not kept with the chat.)";
export function withoutPictures(messages: BetaMessageParam[]): BetaMessageParam[] {
  // a picture a tool showed the agent (read_picture, Features 2, 21 Oct) is one too: the board has it by its address
  const shown = (b: { type: string; content?: unknown }) => b.type === "tool_result" && Array.isArray(b.content) && (b.content as { type?: string }[]).some((c) => c?.type === "image");
  const has = (b: { type: string; content?: unknown }) => b.type === "image" || shown(b);
  if (!messages.some((m) => Array.isArray(m.content) && m.content.some(has))) return messages;
  return messages.map((m) =>
    Array.isArray(m.content) && m.content.some(has)
      ? ({ ...m, content: m.content.map((b) => (b.type === "image" ? { type: "text" as const, text: PICTURE_KEPT } : shown(b) ? { ...b, content: (b as { content: { type: string }[] }).content.map((c) => (c.type === "image" ? { type: "text", text: "(The picture was shown here.)" } : c)) } : b)) } as BetaMessageParam)
      : m,
  );
}

/**
 * One save of a chat for the board. A reply landing is "replied"; seen is when the user last had it in front of them
 * (now for the chat on screen, nothing for one set aside), so a reply that came while they were elsewhere is unread.
 */
function saveOf(body: Saved, p: Provider, seen: number, board: string | null = body.board ?? null): ChatSave {
  const at = body.at ?? Date.now();
  const last = body.messages.at(-1);
  return { id: body.id!, agent: p, title: titleOf(body.messages), at, replied: last?.role === "assistant" ? at : 0, seen, host: sessionHost(body.messages), board, body: keptCopy(body) };
}

/**
 * The chat on screen against the board's copy. One another device wrote in since comes back from the board, unless
 * this page is in the middle of a reply in it; one this page wrote that never reached the board (closed too soon)
 * goes there now.
 */
async function checkBoardCopy() {
  const s = chat.get();
  if (!s.workspaceId || !s.messages.length) return;
  const meta = s.chats.find((c) => c.id === s.chatId);
  if (meta && meta.at > s.chatAt) {
    if (s.status !== "idle" || unsaved(s.chatId)) return;
    const id = s.chatId;
    const got = await readChat(id).catch(() => null);
    const now = chat.get();
    if (!got || now.chatId !== id || now.status !== "idle" || got.chat.at <= now.chatAt) return;
    applySaved({ ...got.body, id, at: got.chat.at }, now.chatBoard);
    closeOpenCalls();
    saveConversation();
  } else if (!meta || meta.at < s.chatAt) saveConversation();
}

/** History from before chats were on the board (16 to 19 Oct 2026), kept in this browser: each agent's list and its chats. */
const OLD_LIST = (ws: string, p: Provider) => `${SAVED(ws, p)}:list`;
const OLD_PAST = (ws: string, id: string, p: Provider) => `${SAVED(ws, p)}:h:${id}`;
const readKept = (k: string): Partial<Saved> | null => readStoredJson<Partial<Saved> | null>(k, null);

/**
 * The chats this browser kept before chats were on the board go there, once: each agent's open chat and its History.
 * Each copy here goes once the board has it (onSaved); one the board already has stays the board's.
 */
function moveToBoard(ws: string) {
  const known = new Set(chat.get().chats.map((c) => c.id));
  for (const p of ["anthropic", "openai"] as Provider[]) {
    const open = readKept(SAVED(ws, p));
    // the one on screen goes with its own check
    if (open?.id && open.id !== chat.get().chatId && Array.isArray(open.messages) && open.messages.length && !known.has(open.id)) saveSoon(saveOf({ ...(open as Saved), id: open.id }, p, open.at ?? 0, null), 0);
    const list = readKept(OLD_LIST(ws, p)) as unknown as { id?: unknown; at?: unknown }[] | null;
    if (!Array.isArray(list)) continue;
    for (const x of list) {
      if (!x || typeof x.id !== "string") continue;
      const past = readKept(OLD_PAST(ws, x.id, p));
      if (!past || !Array.isArray(past.messages)) continue;
      if (known.has(x.id)) dropOldCopy(ws, x.id);
      else {
        const at = typeof past.at === "number" ? past.at : typeof x.at === "number" ? x.at : Date.now();
        saveSoon(saveOf({ ...(past as Saved), id: x.id, at }, p, at, null), 0);
      }
    }
  }
}

/** A chat from before that the board now has: its copy in this browser goes, and its line in the old list. */
function dropOldCopy(ws: string, id: string) {
  for (const p of ["anthropic", "openai"] as Provider[]) {
    // out of reach: it goes next time
    if (readStored(OLD_PAST(ws, id, p)) === null) continue;
    removeStored(OLD_PAST(ws, id, p));
    const list = (readKept(OLD_LIST(ws, p)) as unknown as { id?: unknown }[] | null) ?? [];
    const left = Array.isArray(list) ? list.filter((x) => x && x.id !== id) : [];
    if (left.length) writeStoredJson(OLD_LIST(ws, p), left);
    else removeStored(OLD_LIST(ws, p));
  }
}

/**
 * A save on its way to the board, and the chat's line in the list at once: a new chat shows there the moment it has
 * anything in it, with the name and pin the board gave it kept. Whether its reply is read stays the board's word, so
 * opening a chat always tells the board (markSeen), and every device hears it.
 */
function send(save: ChatSave) {
  if (deleted.has(save.id)) return;
  saveSoon(save);
  const was = chat.get().chats.find((c) => c.id === save.id);
  listChat({ id: save.id, agent: save.agent, title: was?.named ? was.title : save.title, named: !!was?.named, pinned: !!was?.pinned, at: Math.max(save.at, was?.at ?? 0), replied: Math.max(save.replied, was?.replied ?? 0), seen: was ? was.seen : save.seen, host: save.host, board: save.board ?? was?.board ?? null, size: was?.size ?? 0 });
}

/** Now, or just after the board's copy of a chat when this computer's clock is behind the board's. */
function after(id: string): number {
  return Math.max(Date.now(), (chat.get().chats.find((c) => c.id === id)?.at ?? 0) + 1);
}

/**
 * A chat deleted, here, on another device, or let go as the oldest: every copy this browser kept goes, and one on
 * screen gives way to a new chat (said, when it was not this page that deleted it).
 */
function forgetHere(id: string, said: boolean) {
  chat.set((x) => ({ chats: x.chats.filter((c) => c.id !== id) }));
  const ws = chat.get().workspaceId;
  leftHere.delete(id);
  if (ws) {
    for (const p of ["anthropic", "openai"] as Provider[])
      if (readKept(SAVED(ws, p))?.id === id) removeStored(SAVED(ws, p)); // out of reach: it goes next time
    dropOldCopy(ws, id);
    forgetOpen(ws, id);
  }
  const s = chat.get();
  if (s.chatId !== id || s.status === "running") return;
  blankChat();
  saveConversation();
  if (said) toast({ level: "info", title: "That chat was deleted", body: "It was deleted on another device. A new chat is open." });
}

/** A chat's line in the list, as the board says it now. */
function listChat(meta: ChatMeta) {
  chat.set((s) => ({ chats: [meta, ...s.chats.filter((c) => c.id !== meta.id)].sort((a, b) => b.at - a.at) }));
}

// how each save went: the list takes the board's word; a copy older than the board's makes way for it
/** Chats deleted in this visit: a save of one already on its way when it went never brings it back. */
const deleted = new Set<string>();

onSaved((r) => {
  if (deleted.has(r.id)) {
    if (r.ok) void removeChat(r.id).catch(() => {});
    return;
  }
  const ws = chat.get().workspaceId;
  if (r.ok) {
    listChat(r.chat);
    if (ws) dropOldCopy(ws, r.id);
    // the board's time for it, unless a newer save is on its way
    if (!unsaved(r.id)) {
      if (chat.get().chatId === r.id && chat.get().chatAt !== r.chat.at) chat.set({ chatAt: r.chat.at });
      const p = parked.get(r.id);
      if (p) p.chatAt = r.chat.at;
    }
    return;
  }
  if ("gone" in r && r.gone) return forgetHere(r.id, true);
  if (r.stale) {
    listChat(r.chat);
    if (r.id === chat.get().chatId) void checkBoardCopy();
    return;
  }
  // not kept, and not for want of a link (the next save, or the next visit, sends it): said on the chat it was
  if (r.id === chat.get().chatId && r.code !== "network") chat.set({ error: { title: "This chat could not be kept on your board.", body: r.error, action: null } });
});

/** A chat saved, renamed, pinned, read or deleted, on this device or another. */
export function onChatsEvent(ev: { chat?: ChatMeta; deleted?: string }) {
  if (ev.deleted && !deleted.has(ev.deleted)) forgetHere(ev.deleted, true);
  if (ev.chat) {
    listChat(ev.chat);
    if (ev.chat.id === chat.get().chatId) void checkBoardCopy();
  }
}

/** What a tool call that was never answered goes back as. */
export const NOT_RUN = "Not run: the user stopped the chat or reloaded the page before this finished.";
/** What a tool call the user said no to goes back as. */
export const DECLINED = "The user said no. Don't try this again unless they ask.";

const isResult = (b: BetaContentBlockParam): b is BetaToolResultBlockParam => b.type === "tool_result";

/**
 * The answer a call already has in the chat's record, once it finished: what
 * it ran to, its error, or the user's no, worded as when it was given. Null
 * for one that never finished: running, waiting for the user's yes, stopped,
 * or never recorded.
 */
function recordedResult(id: string, t: ToolState | undefined): BetaToolResultBlockParam | null {
  if (t?.status === "ok") return { type: "tool_result", tool_use_id: id, content: t.text || "Done." };
  if (t?.status === "error") return { type: "tool_result", tool_use_id: id, content: t.text || "That did not work.", is_error: true };
  if (t?.status === "declined") return { type: "tool_result", tool_use_id: id, content: DECLINED, is_error: true };
  return null;
}

/**
 * The conversation with every tool call answered. A step's results join the
 * conversation only once all its calls are done, so a reload, or a turn that
 * breaks while one call waits (for the user's yes, or for the board), leaves
 * its calls without them, and both Anthropic and OpenAI refuse every request
 * that carries one. A call that finished goes back with the answer the record
 * holds for it, so nothing that ran is done again; one that never finished
 * goes back as not run. Each goes first in the user message after its call, as
 * Anthropic asks, or in a message of its own when nothing follows. Null when
 * nothing was open. `closed` holds the calls that went back as not run, and
 * `before` the indexes, in the conversation given, that a message was added in
 * front of.
 */
export function answerOpenCalls(messages: BetaMessageParam[], tools: Record<string, ToolState> = {}): { messages: BetaMessageParam[]; closed: string[]; before: number[] } | null {
  const out: BetaMessageParam[] = [];
  const closed: string[] = [];
  const before: number[] = [];
  let any = false;
  let owed: BetaToolResultBlockParam[] | null = null;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (owed && m.role === "user") {
      const parts: BetaContentBlockParam[] = typeof m.content === "string" ? [{ type: "text", text: m.content }] : m.content;
      out.push({ ...m, content: [...parts.filter(isResult), ...owed, ...parts.filter((p) => !isResult(p))] });
      owed = null;
      continue;
    }
    if (owed) {
      out.push({ role: "user", content: owed });
      before.push(i);
      owed = null;
    }
    out.push(m);
    if (m.role !== "assistant" || !Array.isArray(m.content)) continue;
    const calls = m.content.flatMap((b) => (b.type === "tool_use" ? [b.id] : []));
    if (!calls.length) continue;
    const next = messages[i + 1];
    const answered = new Set(next?.role === "user" && Array.isArray(next.content) ? next.content.filter(isResult).map((r) => r.tool_use_id) : []);
    const open = calls.filter((id) => !answered.has(id));
    if (!open.length) continue;
    any = true;
    owed = open.map((id) => {
      const had = recordedResult(id, tools[id]);
      if (had) return had;
      closed.push(id);
      return { type: "tool_result", tool_use_id: id, content: NOT_RUN, is_error: true };
    });
  }
  if (owed) out.push({ role: "user", content: owed });
  return any ? { messages: out, closed, before } : null;
}

/** The call a server tool's result block answers, for the blocks that are one. */
function serverResultOf(b: BetaContentBlockParam): string | null {
  const id = (b as { tool_use_id?: unknown }).tool_use_id;
  return b.type !== "tool_result" && typeof id === "string" ? id : null;
}

/**
 * The conversation with no web search left waiting for its results. A turn
 * Claude paused mid-search goes on when it is sent back, as it is, as the
 * conversation's last word, and the search's results come with the rest of
 * the turn. Once anything follows a turn whose search never got them (the user
 * wrote instead, or the turn could not go on), Anthropic refuses the
 * conversation: that search goes, and what Claude wrote around it stays. A
 * turn's replies one after another are one turn to Anthropic, so a result in
 * the next one counts. Null when there is none to take out.
 */
export function endPausedSearches(messages: BetaMessageParam[]): BetaMessageParam[] | null {
  let out: BetaMessageParam[] | null = null;
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role !== "assistant") continue;
    let j = i;
    while (j + 1 < messages.length && messages[j + 1].role === "assistant") j++;
    if (j === messages.length - 1) break;
    const turn = messages.slice(i, j + 1).flatMap((m) => (Array.isArray(m.content) ? m.content : []));
    const found = new Set(turn.map(serverResultOf).filter((id): id is string => !!id));
    for (let k = i; k <= j; k++) {
      const m = messages[k];
      if (!Array.isArray(m.content)) continue;
      const kept = m.content.filter((b) => b.type !== "server_tool_use" || found.has(b.id));
      if (kept.length === m.content.length) continue;
      out ??= [...messages];
      out[k] = { ...m, content: kept.length ? kept : [{ type: "text", text: "…" }] };
    }
    i = j;
  }
  return out;
}

/**
 * Make the conversation whole before a request: every tool call answered (as
 * it was, or as not run when it never finished), and no web search waiting
 * for results with something after it. True if anything changed.
 */
export function closeOpenCalls(): boolean {
  const s = chat.get();
  const r = answerOpenCalls(s.messages, s.tools);
  const ended = endPausedSearches(r?.messages ?? s.messages);
  if (!r && !ended) return false;
  // an Undo row sits under a message by its index, which a message added in front of it moves on
  const before = r?.before ?? [];
  const at = (n: number) => n + before.filter((b) => b <= n).length;
  chat.set({
    messages: ended ?? r!.messages,
    // only a call that went back as not run says it stopped; one that finished keeps its line
    tools: r?.closed.length ? { ...s.tools, ...Object.fromEntries(r.closed.map((id) => [id, { status: "skipped" as const }])) } : s.tools,
    runs: before.length ? s.runs.map((run) => ({ ...run, at: at(run.at), end: at(run.end) })) : s.runs,
  });
  saveConversation();
  return true;
}

/**
 * The chat on screen, kept: in this browser as its agent's open chat, and on the board once it has anything in it.
 * The user is looking at it, so its replies are read. False when it is too long to keep after a reload.
 */
export function saveConversation(toBoard = true): boolean {
  const s = chat.get();
  if (!s.workspaceId) return true;
  // which chat this browser has open on its board, for this agent: going back to that board opens it again
  if (s.chatBoard) rememberOpen(s.workspaceId, s.chatBoard, s.config.provider, s.messages.length ? s.chatId : "");
  // a save with more messages than the last one is the conversation's latest use: newer than the board's copy
  // whatever this computer's clock says
  let at = s.chatAt;
  if ((writtenAt.get(s.chatId) ?? 0) !== s.messages.length) {
    at = after(s.chatId);
    writtenAt.set(s.chatId, s.messages.length);
    chat.set({ chatAt: at });
  }
  const data = boardBody(s, at);
  let ok = true;
  const text = JSON.stringify(data);
  if (text.length > MAX_SAVED) {
    removeStored(SAVED(s.workspaceId));
    ok = false;
  } else ok = writeStored(SAVED(s.workspaceId), text);
  if (s.messages.length && toBoard) send(saveOf(data, s.config.provider, Date.now()));
  return ok;
}

/**
 * Who wrote the conversation so far, from its own record: a Codex turn keeps
 * what OpenAI sent beside it, a Claude turn does not. Null before any reply.
 * Each provider takes only its own history, so a chat stays with whoever began it.
 */
export function writtenBy(messages: BetaMessageParam[]): Provider | null {
  const first = messages.find((m) => m.role === "assistant");
  return first ? ((first as { oai?: unknown }).oai ? "openai" : "anthropic") : null;
}

/**
 * Claude or Codex: this one's chat is kept, and the other's comes back as it was left. A reply running on the
 * computer goes on in the chat left; one on an API key runs in this page, so the switch waits for it.
 */
export function switchAgent(p: Provider) {
  const s = chat.get();
  if (p === s.config.provider || !canLeave(s)) return;
  // a request still waiting for the one switched from goes in the box for the user to send, never by itself; one
  // waiting for a reply to end goes with that chat, and is sent when it ends
  const waiting = s.status === "running" ? null : s.queued;
  leave("current");
  saveConversation();
  saveSettings({ provider: p });
  chat.set({ config: loadConfig(), models: null, error: null, keyFor: null, queued: null, queuedAgent: null, ...(waiting ? { prefill: waiting } : {}) });
  loadConversation(s.workspaceId, true);
}

/** A chat with nothing in it yet, on screen, on the board open (or the one given). */
function blankChat(board: string | null = appGet().boardId ?? null) {
  placeholder = null;
  chat.set({ messages: [], tools: {}, runs: [], usage: NO_USAGE, draft: null, stopped: null, error: null, approval: null, question: null, hosts: [], pictures: [], spoken: null, turn: null, chatModel: null, handsFree: null, chatId: newId("c_"), chatAt: Date.now(), chatBoard: board, opening: null, heldOn: null });
}

/** A new chat: the one open stays on the board, in the list, its reply going on there. False when it was too long to keep. */
export function newConversation(): boolean {
  const s = chat.get();
  const kept = fitsBoard();
  leave("list");
  if (s.messages.length) {
    saveConversation();
    void saveNow(s.chatId);
  }
  blankChat();
  saveConversation();
  return kept;
}

/** The computer whose agent keeps this conversation's session: the one the last reply came from. */
export function sessionHost(messages: BetaMessageParam[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const r = (messages[i] as { runner?: { host?: string | null } }).runner;
    if (r) return r.host ?? null;
  }
  return null;
}

/** A conversation's opening words, as the list names it until the user names it. */
function titleOf(messages: BetaMessageParam[]): string {
  for (const m of messages) {
    const t = userText(m)?.replace(/\s+/g, " ").trim();
    if (t) return t.length > 120 ? t.slice(0, 119).trimEnd() + "…" : t;
  }
  return "A chat";
}

/** Whether the chat open now can be kept on the board, for New chat and the list to ask before one that can't goes. */
export function fitsBoard(): boolean {
  const s = chat.get();
  return !s.messages.length || new TextEncoder().encode(JSON.stringify(boardBody(s))).length <= BOARD_ROOM;
}

/**
 * Opens a chat from the list, either agent's: the one open stays on the board (its reply going on there), and this
 * one comes back as it was left, or as it is now while its reply runs. "room" when the one open is too long to keep
 * on the board, which goes only if the user says so (force); "gone" when the board no longer has it; "offline" when
 * the board could not be reached; "busy" while a reply runs in this page (on an API key).
 */
export async function openChatById(id: string, force = false): Promise<"ok" | "room" | "gone" | "offline" | "busy"> {
  const s = chat.get();
  if (!s.workspaceId || !canLeave(s)) return "busy";
  if (id === s.chatId) return "ok";
  if (!fitsBoard() && !force) return "room";
  const away = parked.get(id);
  let got: { chat: ChatMeta; body: Saved } | null = null;
  if (!away) {
    try {
      // a reply that just ended here waits a moment to be kept: kept first, so the board's copy has it
      if (unsaved(id)) await saveNow(id);
      got = await readChat(id);
    } catch {
      return "offline";
    }
    if (!got) {
      chat.set((x) => ({ chats: x.chats.filter((c) => c.id !== id) }));
      return "gone";
    }
  }
  // while it was read, the page may have moved on: a reply began here, or this chat opened another way
  let now = chat.get();
  if (!canLeave(now)) return "busy";
  if (now.chatId === id) return "ok";
  // a chat opens on its own board (Fixes 9, 28 Oct): one from another board takes the user there first, so what is
  // asked in it acts on the board it is about. One kept before chats had a board, or whose board is gone, is this one's
  const meta = got?.chat ?? chat.get().chats.find((c) => c.id === id);
  const its = away?.chatBoard ?? meta?.board ?? null;
  const app = appGet();
  const there = its && (app.boards.some((b) => b.id === its) || app.archived.some((b) => b.id === its)) ? its : null;
  const board = there ?? app.boardId ?? null;
  if (there && there !== app.boardId) {
    steering = id;
    try {
      (await import("../state/actions")).switchBoard(there);
    } finally {
      steering = null;
    }
    now = chat.get();
    if (!canLeave(now)) return "busy";
    if (now.chatId === id) return "ok";
  }
  const provider = away?.provider ?? got!.chat.agent;
  const other = provider !== now.config.provider;
  // left for the other agent's chat, the one open stays its own agent's open chat
  leave(other ? "current" : "list");
  if (now.messages.length) {
    saveConversation();
    void saveNow(now.chatId);
  }
  if (other) {
    saveSettings({ provider });
    chat.set({ config: loadConfig(), models: null, keyFor: null, queuedAgent: null });
    // this chat becomes that agent's open one: one of its chats set aside as that is no longer
    for (const x of parked.values()) if (x.provider === provider && x.where === "current") x.where = "list";
  }
  if (!resume(id)) {
    applySaved({ ...got!.body, id, at: got!.chat.at }, board);
    closeOpenCalls();
  } else if (!chat.get().chatBoard) chat.set({ chatBoard: board });
  saveConversation();
  markSeen(id);
  return "ok";
}

// ---------------------------------------------------------------------------
// Each board's own chat (Fixes 9, 28 Oct: "switching boards keeps the last board's conversation open in the chat
// panel ... a request typed there can act on the wrong board"). A chat is on the board it was begun on. Opening a
// board brings up its chat: the one this browser last had open there for this agent, else the board's newest of that
// agent's, else a new one. The chat left goes on in the list, a reply on the computer still going into it. A reply on
// an API key runs in this page, so its chat stays until the reply is done, saying whose board it is on.

/** Which chat this browser had open on each board, for each agent: a chat's id, or "" for a new one. */
const OPEN_ON = (ws: string) => `cb:chat:${ws}:open`;
const OPEN_MAX = 300;
function openMap(ws: string): Record<string, string> {
  const m = readStoredJson<unknown>(OPEN_ON(ws), {});
  return m && typeof m === "object" && !Array.isArray(m) ? (m as Record<string, string>) : {};
}
/** The chat this browser had open on a board for an agent: undefined when it never had one there. */
export function openOn(ws: string, board: string, p: Provider): string | undefined {
  const v = openMap(ws)[`${board}|${p}`];
  return typeof v === "string" ? v : undefined;
}
function rememberOpen(ws: string, board: string, p: Provider, id: string) {
  const m = openMap(ws);
  const k = `${board}|${p}`;
  if (m[k] === id) return;
  delete m[k];
  m[k] = id;
  const keys = Object.keys(m);
  for (const old of keys.slice(0, Math.max(0, keys.length - OPEN_MAX))) delete m[old];
  writeStoredJson(OPEN_ON(ws), m); // out of reach: this visit picks the board's newest
}
function forgetOpen(ws: string, id: string) {
  const m = openMap(ws);
  const gone = Object.keys(m).filter((k) => m[k] === id);
  if (!gone.length) return;
  for (const k of gone) delete m[k];
  writeStoredJson(OPEN_ON(ws), m);
}

/** Chats left on another board in this visit, as they were: going back to one is at once. A few, the newest. */
const leftHere = new Map<string, Convo>();
function keepLeft(s: Convo) {
  if (!s.messages.length) return;
  leftHere.delete(s.chatId);
  leftHere.set(s.chatId, { ...pickConvo(s), status: "idle", turn: null, draft: null, stopped: null, error: null, approval: null, question: null, queued: null, queuedAt: null, spoken: null });
  for (const k of [...leftHere.keys()].slice(0, Math.max(0, leftHere.size - 8))) leftHere.delete(k);
}

/** A board's newest chat of an agent's, as the list has it. */
const newestOn = (board: string, p: Provider, not: string | null = null) => chat.get().chats.find((c) => c.board === board && c.agent === p && c.id !== not && !deleted.has(c.id))?.id ?? null;

/** The new chat put up while a board's own is found: the board's own takes its place, unless the user wrote in it. */
let placeholder: string | null = null;
/** The accounts whose chats this page has read from the board: until then, where a chat kept here belongs is not known. */
const known = new Set<string>();
/** A chat being opened on its board by the user (openChatById): the board's own is not looked for meanwhile. */
let steering: string | null = null;
/** The chat whose next board change is its own doing (chatOpensBoard), and the chat kept on screen on a board it opened. */
let holdFor: { chat: string; at: number } | null = null;
let held: string | null = null;
/** The boards each chat's turns made, as their changes came in (run.ts trackRun). */
const madeBy = new Map<string, Set<string>>();

/**
 * The next board change is the chat on screen's own doing: its agent made a board and opened it, or showed one, or a
 * go-there link in its reply was followed. The chat stays on screen, its reply going on (Fixes 9 fix round).
 */
export function chatOpensBoard() {
  // for the change that follows at once; a link to a place on the board already open changes nothing, and is let go
  holdFor = { chat: chat.get().chatId, at: Date.now() };
}

/** A board a chat's turn made, as it came in: opened by that chat, it becomes the chat's board. */
export function noteMadeBoard(chatId: string, board: string) {
  const set = madeBy.get(chatId) ?? new Set<string>();
  set.add(board);
  madeBy.set(chatId, set);
}

/**
 * The chat on screen, kept on a board it opened itself, takes that board when the user writes in it: what they type is
 * about the board on screen, never another. True when it did.
 */
export function takeHeldBoard(): boolean {
  const s = chat.get();
  const board = appGet().boardId ?? null;
  if (!board || held !== s.chatId || s.heldOn !== board) return false;
  chat.set({ chatBoard: board, heldOn: null });
  held = null;
  return true;
}

/** Whether the chat on screen is on another board than the one open: what is typed in it would not be about this one. */
export function elsewhere(s: Pick<ChatState, "chatBoard" | "heldOn"> = chat.get()): string | null {
  const board = appGet().boardId ?? null;
  // one kept on a board it opened itself is not elsewhere: what is typed in it next is about the board on screen
  return s.chatBoard && board && s.chatBoard !== board && s.heldOn !== board ? s.chatBoard : null;
}

/** The board a chat's turn acts on: its own, else the one open. */
export const chatBoardNow = (): string | null => chat.get().chatBoard ?? appGet().boardId ?? null;

/**
 * The board open brings up its own chat. force: the chat on screen is too long to keep on the board, and the user
 * said it may go. Nothing changes while a reply on an API key runs in this page; it is looked at again when it ends.
 */
export function followBoard(force = false) {
  const s = chat.get();
  const ws = s.workspaceId;
  const board = appGet().boardId ?? null;
  if (!ws || !board || steering) return;
  // a board the chat on screen opened itself keeps the chat on screen, its reply going on; one the chat's own turns
  // made is the chat's board from now on. Only a board the user opens themselves brings up that board's own chat
  const mine = !!holdFor && holdFor.chat === s.chatId && Date.now() - holdFor.at < 2_000;
  holdFor = null;
  if (mine || (s.heldOn === board && held === s.chatId)) {
    held = s.chatId;
    if (s.heldOn !== board) chat.set({ heldOn: board });
    if (s.chatBoard !== board && madeBy.get(s.chatId)?.has(board)) {
      chat.set({ chatBoard: board, heldOn: null });
      if (s.status === "idle") saveConversation();
    }
    return;
  }
  if (s.heldOn) chat.set({ heldOn: null });
  held = null;
  const standIn = s.chatId === placeholder && !s.messages.length && s.status === "idle" && !s.queued;
  if (s.chatBoard === board && !standIn) return;
  // a new chat with nothing in it, from before the board was known, is this board's
  if (!s.chatBoard && !s.messages.length && s.status === "idle" && !standIn) {
    chat.set({ chatBoard: board });
    return;
  }
  if (!canLeave(s) || (!force && !fitsBoard())) return;
  const p = s.config.provider;
  // the chat this browser had open before chats had a board (Fixes 9 fix round): once the board's list says where it
  // is, it comes back on its own board, and on this one when the board has it on none or does not have it at all
  // (it never reached the board): a chat no board claims is never put out of sight
  if (standIn && s.chatBoard === board) {
    const old = readKept(SAVED(ws, p));
    if (old?.id && typeof old.board !== "string" && Array.isArray(old.messages) && old.messages.length && !deleted.has(old.id)) {
      if (!known.has(ws)) return;
      const meta = s.chats.find((c) => c.id === old.id);
      if (!meta || !meta.board || meta.board === board) {
        showBoardChat(ws, board, p, old.id);
        // kept on the board as this board's
        saveConversation();
        return;
      }
    }
  }
  // the chat this browser had open here, unless it went to another board since (it carried on there)
  const was = openOn(ws, board, p);
  const gone = !!was && ((was === s.chatId ? s.chatBoard : null) ?? s.chats.find((c) => c.id === was)?.board ?? board) !== board;
  const kept = gone ? undefined : was;
  // the chat on screen, gone on to another board, is not this one's, whatever the list still says of it
  const id = kept === undefined ? newestOn(board, p, s.chatBoard && s.chatBoard !== board ? s.chatId : null) : kept || null;
  if (standIn && s.chatBoard === board && (!id || id === s.chatId)) return;
  if (id && id === s.chatId) {
    chat.set({ chatBoard: board });
    return;
  }
  // a new chat the user started there stays new; none known yet stands in until the board's list says
  showBoardChat(ws, board, p, id, kept !== "");
}

/** The chat on screen goes on in the list, and this board's (by id, or a new one) takes its place. stand: a new one stands in for the board's own. */
function showBoardChat(ws: string, board: string, p: Provider, id: string | null, stand = true) {
  const s = chat.get();
  // a request waiting to be sent goes with the board it was asked on, into its chat
  const waiting = s.status === "idle" && s.queued ? { queued: s.queued, queuedAt: s.queuedAt, spoken: s.spoken } : null;
  leave("list");
  if (s.messages.length && s.chatId !== id) {
    saveConversation();
    void saveNow(s.chatId);
    if (s.status !== "running") keepLeft(s);
  }
  const kept = readKept(SAVED(ws, p));
  const left = id ? leftHere.get(id) : undefined;
  if (id && resume(id)) {
    if (!chat.get().chatBoard) chat.set({ chatBoard: board });
  } else if (left) {
    leftHere.delete(id!);
    chat.set({ ...left, opening: null, heldOn: null });
    void checkBoardCopy();
  } else if (id && kept?.id === id && Array.isArray(kept.messages)) {
    applySaved(kept, board);
    closeOpenCalls();
    void checkBoardCopy();
  } else {
    blankChat(board);
    if (stand || id) placeholder = chat.get().chatId;
    if (id) {
      // read from the board: the panel says it is opening it meanwhile, and a word typed first keeps the new chat
      const standIn = chat.get().chatId;
      chat.set({ opening: id });
      void readChat(id).then(
        (got) => {
          const now = chat.get();
          if (now.chatId !== standIn || now.messages.length || now.status !== "idle" || appGet().boardId !== board) return void (now.opening === id && chat.set({ opening: null }));
          if (!got) {
            forgetOpen(ws, id);
            chat.set((x) => ({ opening: null, chats: x.chats.filter((c) => c.id !== id) }));
            return followBoard();
          }
          placeholder = null;
          applySaved({ ...got.body, id, at: got.chat.at }, board);
          closeOpenCalls();
          saveConversation(false);
          markSeen(id);
        },
        () => {
          if (chat.get().opening === id) chat.set({ opening: null });
        },
      );
    }
  }
  if (waiting) chat.set(waiting);
  saveConversation(false);
  markSeen(chat.get().chatId);
}

// a board opened, here or by a link: its chat comes up. And a reply on a key that kept its chat on screen past a board
// change lets the board's chat come once it ends
useApp.subscribe((s, prev) => {
  if (s.boardId !== prev.boardId) queueMicrotask(() => followBoard());
});
useChat.subscribe((s, prev) => {
  if (prev.status === "running" && s.status === "idle" && elsewhere(s)) queueMicrotask(() => followBoard());
});

/**
 * The chat on screen brought up to the board's copy before a reply its computer is giving is followed into it: a page
 * whose copy is older (another device went on with the chat) takes the board's first, so the reply never lands on an
 * old copy that would then be saved over the newer one.
 */
export async function freshForReply(id: string): Promise<void> {
  await listing?.catch(() => {});
  const s = chat.get();
  if (s.chatId !== id || unsaved(id)) return;
  const meta = s.chats.find((c) => c.id === id);
  if (!meta || meta.at <= s.chatAt) return;
  const got = await readChat(id).catch(() => null);
  const now = chat.get();
  if (!got || now.chatId !== id || now.status === "running") return;
  applySaved({ ...got.body, id, at: got.chat.at }, now.chatBoard);
  closeOpenCalls();
}

/** A chat whose reply came while the user was elsewhere, read now that it is open. */
function markSeen(id: string) {
  const meta = chat.get().chats.find((c) => c.id === id);
  if (!meta || meta.replied <= meta.seen) return;
  const seen = Date.now();
  listChat({ ...meta, seen });
  void patchChat(id, { seen }).then(listChat, () => {});
}

/**
 * A chat deleted from the board and the list. One whose reply still runs stays; the one open gives way to a new
 * chat. False when it was not deleted.
 */
export async function deleteChat(id: string): Promise<boolean> {
  const s = chat.get();
  if (parked.has(id) || (s.chatId === id && s.status === "running")) return false;
  deleted.add(id);
  forgetHere(id, false);
  try {
    await removeChat(id);
    return true;
  } catch {
    deleted.delete(id);
    void readChatList();
    return false;
  }
}

/** A chat named by the user; an empty name gives it back its first message. */
export async function renameChat(id: string, title: string): Promise<boolean> {
  const meta = chat.get().chats.find((c) => c.id === id);
  if (meta) listChat({ ...meta, title: title.trim() || meta.title, named: !!title.trim() });
  try {
    listChat(await patchChat(id, { title }));
    return true;
  } catch {
    void readChatList();
    return false;
  }
}

/** A chat pinned to the top of the list, or let go back to its day. */
export async function pinChat(id: string, pinned: boolean): Promise<boolean> {
  const meta = chat.get().chats.find((c) => c.id === id);
  if (meta) listChat({ ...meta, pinned });
  try {
    listChat(await patchChat(id, { pinned }));
    return true;
  } catch {
    void readChatList();
    return false;
  }
}

// ---------------------------------------------------------------------------
// Replies that go on off screen (David, 19 Oct 2026: he could not go back to his Claude chat while a Codex reply
// ran). A chat whose reply runs on the user's computer can be left, for the other agent, a new chat or an earlier
// one, and the reply goes on into it: every event of it is written through convo(), never into the chat on screen.
// Opened again, it is as it is now. A reply on an API key runs in this page, so its chat waits on screen for it.

/** What one conversation holds, as against the panel and its settings. */
const CONVO_KEYS = ["messages", "tools", "runs", "usage", "hosts", "pictures", "chatModel", "chatId", "chatAt", "chatBoard", "draft", "stopped", "error", "approval", "question", "status", "queued", "queuedAt", "spoken", "turn", "handsFree"] as const;
export type Convo = Pick<ChatState, (typeof CONVO_KEYS)[number]>;
const pickConvo = (s: Convo): Convo => Object.fromEntries(CONVO_KEYS.map((k) => [k, s[k]])) as unknown as Convo;

/**
 * A chat left while its reply runs: what it holds, whose it is, the model a message waiting in it goes with, and
 * whether it is still its agent's open chat in this browser (left for the other agent) or only in the list.
 */
export interface Parked extends Convo {
  provider: Provider;
  model: string;
  effort: Effort | null;
  ws: string;
  where: "current" | "list";
}
const parked = new Map<string, Parked>();

/**
 * A model picked in the chat's menu: for this chat alone (Features 4, 26 Oct). The agent's default, picked again, puts
 * the chat back on following the default. A chat with messages is kept at once, so the board and every device have it.
 */
export function pickChatModel(model: string) {
  const s = chat.get();
  const d = modelFor(s.config.provider);
  chat.set({ chatModel: model === d ? null : model });
  if (s.messages.length) saveConversation();
}

/** Whether the chat on screen can be left: it is not running a reply in this page. */
export const canLeave = (s: ChatState = chat.get()) => s.status !== "running" || !!s.turn;

/** When the user last pressed Stop: what the board starts by itself (a plan's "carry on") never restarts a chat they stopped. */
let lastStop = 0;
export function markStopped() {
  lastStop = Date.now();
}
export const stoppedSince = (t: number) => lastStop >= t;

/** Whether the conversation open here holds the user's words with this in them: the chat a request was sent to. */
export function chatHolds(needle: string, messages = chat.get().messages): boolean {
  return messages.some((m) => m.role === "user" && (typeof m.content === "string" ? m.content.includes(needle) : Array.isArray(m.content) && m.content.some((b) => b.type === "text" && b.text.includes(needle))));
}

/**
 * The chat on screen is being left. One whose reply runs on the computer is set aside with all it holds (a message
 * waiting to follow the reply too), and the screen starts from nothing of it. True when the screen is free.
 */
function leave(where: Parked["where"]): boolean {
  const s = chat.get();
  if (s.status !== "running") return true;
  if (!s.turn || !s.workspaceId) return false;
  parked.set(s.chatId, { ...pickConvo(s), provider: s.config.provider, model: modelFor(s.config.provider, s.chatModel)!, effort: s.config.effort, ws: s.workspaceId, where });
  chat.set({ status: "idle", turn: null, draft: null, stopped: null, error: null, approval: null, question: null, queued: null, spoken: null });
  refreshAway();
  return true;
}

/** A chat set aside comes back on screen as it is now. False when it is not one. */
function resume(id: string): boolean {
  const p = parked.get(id);
  if (!p || p.ws !== chat.get().workspaceId) return false;
  parked.delete(id);
  chat.set({ ...pickConvo(p), heldOn: null });
  refreshAway();
  return true;
}

/** A chat left while its reply ran, while it is still set aside. */
export const parkedChat = (id: string): Parked | null => parked.get(id) ?? null;

/**
 * A new chat that runs set aside from its first message, the chat on screen left as it is: a board button's task
 * (Features 2, 21 Oct). It is in the Chats list once its message is kept, and its reply lands in it. Null with no board.
 */
export function startAway(provider: Provider, model: string, effort: Effort | null, own: string | null = null): ConvoRef | null {
  const ws = chat.get().workspaceId;
  if (!ws) return null;
  const id = newId("c_");
  // own: a Button's own model, which its chat keeps for whatever the user writes in it next
  parked.set(id, { messages: [], tools: {}, runs: [], usage: NO_USAGE, hosts: [], pictures: [], chatModel: own, chatId: id, chatAt: Date.now(), chatBoard: appGet().boardId ?? null, draft: null, stopped: null, error: null, approval: null, question: null, status: "idle", queued: null, queuedAt: null, spoken: null, turn: null, provider, model, effort, ws, where: "list" });
  return convo(id);
}

/**
 * A chat set aside to take a reply its computer is still giving, or gave while the page was closed: the newer of its
 * copy in this browser and the board's. The chat on screen is itself. Null when neither has it; it throws when the
 * board could not be reached and this browser has none, for the reply to be picked up later.
 */
export async function parkSaved(id: string, p: Provider, turn: string): Promise<ConvoRef | null> {
  const s = chat.get();
  if (!s.workspaceId) return null;
  if (s.chatId === id || parked.has(id)) return convo(id);
  const ws = s.workspaceId;
  await listing?.catch(() => {});
  const kept = readKept(SAVED(ws, p));
  const where: Parked["where"] = kept?.id === id ? "current" : "list";
  let saved: Partial<Saved> | null = where === "current" ? kept : null;
  const meta = chat.get().chats.find((c) => c.id === id);
  if (!saved || (meta && meta.at > (saved.at ?? 0))) {
    try {
      const got = await readChat(id);
      if (got) saved = { ...got.body, id, at: got.chat.at };
    } catch (e) {
      // the board could not be reached: the copy here when there is one, else the reply is picked up later
      if (!saved) throw e;
    }
  }
  // while it was read, it may have opened, or been set aside another way
  const now = chat.get();
  if (now.chatId === id || parked.has(id)) return convo(id);
  if (!saved || !Array.isArray(saved.messages) || now.workspaceId !== ws) return null;
  const cfg = loadConfig();
  // on its own board, as the list has it; one kept before chats had a board keeps none until it is opened
  const back = savedConvo({ ...saved, id }, typeof saved.board === "string" ? saved.board : (chat.get().chats.find((c) => c.id === id)?.board ?? null));
  parked.set(id, { ...back, status: "running", queued: null, queuedAt: null, spoken: null, turn, provider: p, model: modelFor(p, back.chatModel)!, effort: cfg.provider === p ? cfg.effort : null, ws, where });
  refreshAway();
  return convo(id);
}

type ConvoPatch = Partial<Convo> | ((c: Convo) => Partial<Convo>);

/** One conversation, wherever it is: on screen, or set aside while its reply runs. Null once it is neither. */
export function convo(id: string) {
  return {
    id,
    here: () => chat.get().chatId === id,
    get: (): Convo | null => (chat.get().chatId === id ? chat.get() : (parked.get(id) ?? null)),
    set: (patch: ConvoPatch) => {
      if (chat.get().chatId === id) return chat.set(patch as Partial<ChatState> | ((s: ChatState) => Partial<ChatState>));
      const p = parked.get(id);
      if (!p) return;
      Object.assign(p, typeof patch === "function" ? patch(p) : patch);
      refreshAway();
    },
  };
}
export type ConvoRef = ReturnType<typeof convo>;

/**
 * Keeps a conversation: the one on screen as its agent's open chat and on the board; one set aside on the board, and
 * in this browser while it is still its agent's open chat. One set aside whose reply is over leaves the set aside: from
 * now on it is opened from the board.
 */
export function saveConvo(id: string): boolean {
  if (chat.get().chatId === id) return saveConversation();
  const p = parked.get(id);
  if (!p) return true;
  if ((writtenAt.get(id) ?? 0) !== p.messages.length) {
    p.chatAt = after(id);
    writtenAt.set(id, p.messages.length);
  }
  let ok = true;
  const data = boardBody(p);
  if (p.where === "current") {
    const text = JSON.stringify(data);
    ok = text.length <= MAX_SAVED && writeStored(SAVED(p.ws, p.provider), text);
  }
  // the user was elsewhere: a reply in it waits to be read
  if (p.messages.length) send(saveOf(data, p.provider, 0));
  if (p.status !== "running" && !p.queued) {
    parked.delete(id);
    refreshAway();
  }
  return ok;
}

let awayNow = "";
function refreshAway() {
  const ws = chat.get().workspaceId;
  // only what is really going on: a chat whose reply has ended is on its way out of the set aside
  const list: AwayChat[] = [...parked.values()].filter((p) => p.ws === ws && (p.status === "running" || p.question || p.approval)).map((p) => ({ id: p.chatId, provider: p.provider, title: titleOf(p.messages), state: p.question || p.approval ? "needs_you" : "working" }));
  const key = JSON.stringify(list);
  if (key === awayNow) return;
  awayNow = key;
  chat.set({ away: list });
}

/** Signing out takes the conversation and both keys with it. */
export function forgetEverything() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith("cb:chat:")) localStorage.removeItem(k);
    }
  } catch {
    /* nothing kept */
  }
  saveKey(null, "anthropic");
  saveKey(null, "openai");
  parked.clear();
  leftHere.clear();
  placeholder = null;
  awayNow = "";
  forgetSaves();
  listed.clear();
  known.clear();
  chat.set({ messages: [], tools: {}, runs: [], usage: NO_USAGE, config: loadConfig(), open: false, workspaceId: null, models: null, keyFor: null, pictures: [], chats: [], chatsError: null, away: [], turn: null, chatModel: null, handsFree: null, chatId: newId("c_"), chatAt: Date.now(), chatBoard: null, opening: null });
}

// ---------------------------------------------------------------------------
// what the panel shows of a message

export const CONTEXT_TAG = "<board_context>";

/** The words the user typed, without the board context the panel adds. */
export function userText(m: BetaMessageParam): string | null {
  if (m.role !== "user") return null;
  if (typeof m.content === "string") return m.content.startsWith(CONTEXT_TAG) ? null : m.content;
  const parts = m.content.filter((b): b is { type: "text"; text: string } => b.type === "text" && !b.text.startsWith(CONTEXT_TAG)).map((b) => b.text);
  return parts.length ? parts.join("\n") : null;
}
